import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import {
    DollarSign,
    TrendingUp,
    Wallet,
    PieChart,
    Loader2,
    AlertCircle,
    ArrowUpRight,
    History,
    Info,
    Bookmark,
    CreditCard
} from 'lucide-react';

const Earnings = ({ authToken, setActiveTab, setWithdrawalFilter, withdrawalRequests }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [earningsData, setEarningsData] = useState({
        total_revenue: 0,
        platform_commission: 0,
        payout_pending: 0,
        ticket_revenue: 0,
        top_performing_categories: []
    });

    useEffect(() => {
        const fetchEarnings = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await fetch(`${API_BASE_URL}/admin/super/earnings`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });

                if (!response.ok) {
                    throw new Error('Failed to load earnings data');
                }

                const data = await response.json();
                setEarningsData({
                    total_revenue: data.total_revenue ?? 0,
                    platform_commission: data.platform_commission ?? 0,
                    payout_pending: data.payout_pending ?? 0,
                    payout_paid: data.payout_paid ?? 0,
                    ticket_revenue: data.ticket_revenue ?? 0,
                    top_performing_categories: Array.isArray(data.top_performing_categories) ? data.top_performing_categories : []
                });
            } catch (err) {
                setError(err.message || 'Failed to load earnings');
                // Mock data for demonstration if API fails or is not yet implemented
                setEarningsData({
                    total_revenue: 5600000,
                    platform_commission: 840000,
                    payout_pending: 4760000,
                    payout_paid: 3920000,
                    ticket_revenue: 450000,
                    top_performing_categories: [
                        { id: 1, name: "Tattoo Art", revenue: 1200000 },
                        { id: 2, name: "Oil Painting", revenue: 850000 },
                        { id: 3, name: "Digital Art", revenue: 640000 },
                        { id: 4, name: "Sketching", revenue: 420000 }
                    ]
                });
            } finally {
                setLoading(false);
            }
        };

        fetchEarnings();
    }, [authToken]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
            </div>
        );
    }

    const stats = [
        {
            label: 'Total Revenue',
            value: formatCurrency(earningsData.total_revenue),
            icon: DollarSign,
            color: 'bg-blue-50 text-blue-700 border-blue-100',
            trend: '+12.5%',
            desc: 'Overall platform sales'
        },
        {
            label: 'Ticket Revenue',
            value: formatCurrency(earningsData.ticket_revenue),
            icon: Bookmark,
            color: 'bg-indigo-50 text-indigo-700 border-indigo-100',
            trend: 'Direct',
            desc: 'Event ticket sales'
        },
        {
            label: 'Platform Commission',
            value: formatCurrency(earningsData.platform_commission),
            icon: TrendingUp,
            color: 'bg-purple-50 text-purple-700 border-purple-100',
            trend: '+8.2%',
            desc: 'Platform earnings (15%)'
        },
        {
            label: 'Payout Pending',
            value: formatCurrency(withdrawalRequests.reduce((acc, req) => req.status === 'Pending' ? acc + req.amount : acc, 0)),
            icon: Wallet,
            color: 'bg-amber-50 text-amber-700 border-amber-100',
            trend: 'Active',
            desc: 'Owed to providers',
            link: 'Pending'
        },
        {
            label: 'Payout Paid',
            value: formatCurrency(withdrawalRequests.reduce((acc, req) => req.status === 'Paid' ? acc + req.amount : acc, 0)),
            icon: History,
            color: 'bg-green-50 text-green-700 border-green-100',
            trend: 'Settled',
            desc: 'Paid transactions',
            link: 'Paid'
        }
    ];

    return (
        <div className="max-w-7xl mx-auto p-6 space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Earnings Overview</h2>
                    <p className="text-gray-500">Track platform revenue, commissions, and payouts.</p>
                </div>
                <button
                    onClick={() => setActiveTab('withdrawals')}
                    className="bg-[#6366F1] text-white px-6 py-3 rounded-xl font-bold flex items-center gap-3 shadow-lg shadow-indigo-100 hover:bg-indigo-600 transition-all active:scale-95 group"
                >
                    <CreditCard className="w-5 h-5" />
                    View Withdrawal Requests
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
            </div>

            {error && (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-center text-amber-700">
                    <Info className="h-5 w-5 mr-3 flex-shrink-0" />
                    <p className="text-sm italic">Showing simulated data. Backend endpoint /admin/super/earnings not responding.</p>
                </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                {stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <div
                            key={index}
                            className={`p-6 rounded-2xl border transition hover:shadow-lg ${stat.color} ${stat.link ? 'cursor-pointer hover:border-current' : ''}`}
                            onClick={() => {
                                if (stat.link) {
                                    setWithdrawalFilter(stat.link);
                                    setActiveTab('withdrawals');
                                }
                            }}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className={`p-2 rounded-lg ${stat.color.replace('50', '100')}`}>
                                    <Icon className="h-6 w-6" />
                                </div>
                                {stat.trend && (
                                    <span className="text-xs font-bold px-2 py-1 rounded-full bg-white/50 border border-current opacity-70">
                                        {stat.trend}
                                    </span>
                                )}
                            </div>
                            <p className="text-sm font-medium opacity-80">{stat.label}</p>
                            <p className="text-2xl font-bold mt-1 tracking-tight">{stat.value}</p>
                            <p className="text-xs mt-3 opacity-60 flex items-center">
                                <Info className="h-3 w-3 mr-1" />
                                {stat.desc}
                            </p>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Top performing categories */}
                <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-6 py-5 border-b border-gray-50 flex items-center justify-between">
                        <h3 className="font-bold text-gray-900 flex items-center">
                            <PieChart className="h-5 w-5 mr-2 text-indigo-500" />
                            Top Performing Categories
                        </h3>
                        <button className="text-xs font-bold text-indigo-600 hover:text-indigo-700">View All</button>
                    </div>
                    <div className="p-6">
                        <div className="space-y-6">
                            {(earningsData.top_performing_categories || []).map((cat, idx) => {
                                const totalRev = earningsData.total_revenue || 1;
                                const percentage = (cat.revenue / totalRev) * 100;
                                return (
                                    <div key={cat.id || idx}>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-bold text-gray-700">{cat.name}</span>
                                            <span className="text-sm font-bold text-gray-900">{formatCurrency(cat.revenue)}</span>
                                        </div>
                                        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-indigo-500 rounded-full transition-all duration-1000"
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div>
                                        <p className="text-[10px] text-gray-400 mt-1">{percentage.toFixed(1)}% of total revenue</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Quick Report / Summary */}
                <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-6 text-white shadow-xl shadow-indigo-100 flex flex-col justify-between">
                    <div>
                        <h3 className="text-lg font-bold mb-2">Earnings Report</h3>
                        <p className="text-indigo-100 text-sm mb-6">Your platform has seen a healthy 15% growth in artist commissions this month.</p>

                        <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                                    <ArrowUpRight className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="text-xs text-indigo-200">Growth Rate</p>
                                    <p className="font-bold">+18.4% YoY</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                                    <TrendingUp className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="text-xs text-indigo-200">Active Providers</p>
                                    <p className="font-bold">124 Artists</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button className="w-full bg-white text-indigo-600 py-3 rounded-xl font-bold mt-8 shadow-lg hover:bg-gray-50 transition-colors">
                        Download Detailed Report
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Earnings;
